import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DIFFICULTIES } from "@/lib/api";

const ALL = "__all";

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs font-semibold uppercase tracking-wider text-ink-3">{label}</Label>
    {children}
  </div>
);

const lockTag = <span className="ml-1 text-[10px] font-semibold uppercase text-ink-3">· upgrade</span>;

export function SelectionForm({ subjects, value, onChange, topicRequired = true, counts, showSource = false, limits }) {
  const subjOk = (id) => !limits?.subject_ids || limits.subject_ids.includes(id);
  const diffOk = (d) => !limits || limits.difficulties.includes(d);
  const countOk = (c) => !limits || !showSource || c <= limits.max_quiz_questions;
  const subject = subjects.find((s) => s.id === value.subject_id);
  const set = (k, v) => onChange({ ...value, [k]: v, ...(k === "subject_id" ? { topic: "" } : {}) });
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Field label="Subject">
        <Select value={value.subject_id || undefined} onValueChange={(v) => set("subject_id", v)}>
          <SelectTrigger data-testid="select-subject"><SelectValue placeholder="Choose subject" /></SelectTrigger>
          <SelectContent>
            {subjects.map((s) => <SelectItem data-testid={`subject-option-${s.id}`} key={s.id} value={s.id} disabled={!subjOk(s.id)}>{s.name}{!subjOk(s.id) && lockTag}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Topic">
        <Select value={value.topic || (topicRequired ? undefined : ALL)} onValueChange={(v) => set("topic", v === ALL ? "" : v)} disabled={!subject}>
          <SelectTrigger data-testid="select-topic"><SelectValue placeholder="Choose topic" /></SelectTrigger>
          <SelectContent>
            {!topicRequired && <SelectItem value={ALL}>All topics (mixed)</SelectItem>}
            {subject?.topics.map((t, i) => <SelectItem data-testid={`topic-option-${i}`} key={t.id} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Difficulty">
        <Select value={value.difficulty} onValueChange={(v) => set("difficulty", v)}>
          <SelectTrigger data-testid="select-difficulty" className="capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DIFFICULTIES.map((d) => <SelectItem data-testid={`difficulty-option-${d}`} key={d} value={d} className="capitalize" disabled={!diffOk(d)}>{d}{!diffOk(d) && lockTag}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      {counts && (
        <Field label={showSource ? "Questions" : "Cards"}>
          <div className="flex gap-2">
            {counts.map((c) => (
              <button key={c} type="button" data-testid={`count-option-${c}`} onClick={() => set("count", c)} disabled={!countOk(c)} title={countOk(c) ? undefined : "Upgrade to unlock"}
                className={`h-10 flex-1 rounded-md border text-sm font-semibold transition-colors ${value.count === c ? "border-brand bg-solid text-white" : "border-line bg-surface text-ink-2 hover:border-ink-3"} disabled:cursor-not-allowed disabled:opacity-40`}>
                {c}
              </button>
            ))}
          </div>
        </Field>
      )}
      {showSource && (
        <div className="sm:col-span-2 lg:col-span-4">
          <div className="inline-flex rounded-lg border border-line bg-canvas-2 p-1">
            {[["ai", "Fresh AI questions"], ["bank", "Approved question bank"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`source-option-${k}`} onClick={() => set("source", k)}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${value.source === k ? "bg-surface text-ink shadow-sm" : "text-ink-3 hover:text-ink"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
